import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../../components/SEO/PageMeta';
import SyntaxHighlightedCode from '../../components/SyntaxHighlightedCode/SyntaxHighlightedCode';
import { insertIndent, insertSmartNewLine } from '../../utils/codeEditing';
import styles from './JavaScriptEditorPage.module.css';

type OutputType = 'error' | 'console';

interface OutputLine {
  id: number;
  type: OutputType;
  text: string;
}

const DEFAULT_CODE = `console.log("Welcome to the online JavaScript editor");`;

const JS_SUGGESTIONS = [
  'Array.from',
  'console.log',
  'Date.now',
  'document.querySelector',
  'fetch',
  'filter',
  'find',
  'forEach',
  'JSON.parse',
  'JSON.stringify',
  'localStorage.getItem',
  'localStorage.setItem',
  'map',
  'Math.max',
  'Math.min',
  'Object.entries',
  'Object.keys',
  'Object.values',
  'Promise.all',
  'reduce',
  'setTimeout',
  'sort',
  'URLSearchParams',
  'window.location',
  'async',
  'await',
  'const',
  'let',
  'return',
];

export default function JavaScriptEditorPage() {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const contextRef = useRef<Record<string, unknown>>({});
  const [code, setCode] = useState(DEFAULT_CODE);
  const [outputs, setOutputs] = useState<OutputLine[]>([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [editorScrollTop, setEditorScrollTop] = useState(0);
  const [editorScrollLeft, setEditorScrollLeft] = useState(0);

  const currentToken = useMemo(
    () => getCurrentToken(code, textareaRef.current?.selectionStart ?? code.length),
    [code],
  );
  const lineNumbers = useMemo(
    () => Array.from({ length: code.split('\n').length }, (_, index) => index + 1),
    [code],
  );
  const suggestions = useMemo(() => {
    if (!currentToken || currentToken.length < 2) {
      return [];
    }

    return JS_SUGGESTIONS.filter((item) =>
      item.toLowerCase().startsWith(currentToken.toLowerCase()),
    ).slice(0, 8);
  }, [currentToken]);

  async function runCode() {
    const logs: string[] = [];

    try {
      await evaluateJavaScript(code, contextRef.current, logs);
      setOutputs(logs.map((log) => makeOutput('console', log)));
    } catch (error) {
      setOutputs([
        ...logs.map((log) => makeOutput('console', log)),
        makeOutput('error', error instanceof Error ? error.message : String(error)),
      ]);
    }
  }

  function handleFormat() {
    setCode(formatJavaScript(code));
  }

  function handleEditorKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      void runCode();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      applyTextEdit(insertSmartNewLine);
      return;
    }

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      handleFormat();
      return;
    }

    if (event.key === 'Tab' && suggestions.length > 0) {
      event.preventDefault();
      applySuggestion(suggestions[activeSuggestionIndex] ?? suggestions[0]);
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      applyTextEdit(insertIndent);
      return;
    }

    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setActiveSuggestionIndex((current) => (current + 1) % suggestions.length);
      return;
    }

    if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setActiveSuggestionIndex(
        (current) => (current - 1 + suggestions.length) % suggestions.length,
      );
      return;
    }
  }

  function applySuggestion(suggestion: string) {
    const textarea = textareaRef.current;
    const cursor = textarea?.selectionStart ?? code.length;
    const tokenStart = cursor - currentToken.length;
    const nextCode = `${code.slice(0, tokenStart)}${suggestion}${code.slice(cursor)}`;
    const nextCursor = tokenStart + suggestion.length;

    setCode(nextCode);
    window.requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.selectionStart = nextCursor;
      textareaRef.current.selectionEnd = nextCursor;
      textareaRef.current.focus();
    });
  }

  function applyTextEdit(
    edit: (value: string, start: number, end: number) => { value: string; cursor: number },
  ) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? code.length;
    const end = textarea?.selectionEnd ?? start;
    const next = edit(code, start, end);

    setCode(next.value);
    window.requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.selectionStart = next.cursor;
      textareaRef.current.selectionEnd = next.cursor;
      textareaRef.current.focus();
    });
  }

  return (
    <>
      <PageMeta
        title="Online JavaScript Editor"
        description="Write, format, autocomplete, and run JavaScript online in a browser sandbox with code on the left and output on the right."
        path="/online-javascript-editor"
      />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden> / </span>
        <span>Online JavaScript Editor</span>
      </nav>

      <section className={styles.hero}>
        <p className={styles.eyebrow}>Browser JavaScript runner</p>
        <h1>Online JavaScript Editor</h1>
        <p>
          Write JavaScript on the left, run it in a browser sandbox, and inspect the output on the
          right. Use formatting and autocomplete suggestions for faster editing.
        </p>
      </section>

      <section className={styles.editorShell} aria-label="Online JavaScript editor">
        <div className={styles.panel}>
          <header className={styles.panelHeader}>
            <h2>Code</h2>
            <div className={styles.actions}>
              <button type="button" className={styles.button} onClick={handleFormat}>
                Format
              </button>
              <button type="button" className={styles.button} onClick={() => void runCode()}>
                Run
              </button>
            </div>
          </header>
          <div className={styles.editorWrap}>
            <div className={styles.codeFrame}>
              <div className={styles.lineNumberGutter} aria-hidden>
                <div style={{ transform: `translateY(-${editorScrollTop}px)` }}>
                  {lineNumbers.map((lineNumber) => (
                    <span key={lineNumber}>{lineNumber}</span>
                  ))}
                </div>
              </div>
              <div className={styles.codePane}>
                <div className={styles.highlightLayer}>
                  <div
                    style={{
                      transform: `translate(${-editorScrollLeft}px, ${-editorScrollTop}px)`,
                    }}
                  >
                    <SyntaxHighlightedCode code={code} ariaHidden />
                  </div>
                </div>
                <textarea
                  ref={textareaRef}
                  className={styles.editor}
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value);
                    setActiveSuggestionIndex(0);
                  }}
                  onScroll={(event) => {
                    setEditorScrollTop(event.currentTarget.scrollTop);
                    setEditorScrollLeft(event.currentTarget.scrollLeft);
                  }}
                  onKeyDown={handleEditorKeyDown}
                  spellCheck={false}
                  aria-label="JavaScript code editor"
                />
              </div>
            </div>
            {suggestions.length > 0 && (
              <div className={styles.suggestions} aria-label="JavaScript suggestions">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion}
                    type="button"
                    className={`${styles.suggestion} ${
                      index === activeSuggestionIndex ? styles.activeSuggestion : ''
                    }`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      applySuggestion(suggestion);
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className={styles.hint}>
            Shortcuts: Cmd/Ctrl + Enter to run, Cmd/Ctrl + S to format, Tab to accept autocomplete
            or indent.
          </p>
        </div>

        <div className={styles.panel}>
          <header className={styles.panelHeader}>
            <h2>Output</h2>
            <button type="button" className={styles.button} onClick={() => setOutputs([])}>
              Clear
            </button>
          </header>
          <div className={styles.output} aria-live="polite">
            {outputs.length === 0 ? (
              <pre className={styles.empty}>Run code to see output here.</pre>
            ) : (
              <div className={styles.logs}>
                {outputs.map((output) => (
                  <div
                    key={output.id}
                    className={`${styles.logLine} ${styles[`${output.type}Line`]}`}
                  >
                    <span className={styles.outputLabel}>{getOutputLabel(output.type)}</span>
                    <div className={styles.outputValue}>
                      {output.type === 'error' ? (
                        <pre>{output.text}</pre>
                      ) : (
                        <SyntaxHighlightedCode code={output.text} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function getOutputLabel(type: OutputType): string {
  if (type === 'console') return 'Console';
  return 'Error';
}

async function evaluateJavaScript(
  code: string,
  context: Record<string, unknown>,
  logs: string[],
): Promise<void> {
  const sandbox = createSandbox(context, logs);
  const statementResult = await tryEvaluate(code, sandbox);

  if (!statementResult.ok) {
    throw statementResult.error;
  }
}

async function tryEvaluate(
  body: string,
  sandbox: Record<string | symbol, unknown>,
): Promise<{ ok: true; value: unknown } | { ok: false; error: Error }> {
  try {
    const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
    const runner = new AsyncFunction('sandbox', `with (sandbox) { ${body} }`);
    return { ok: true, value: await runner(sandbox) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error : new Error(String(error)) };
  }
}

function createSandbox(context: Record<string, unknown>, logs: string[]) {
  const consoleProxy = {
    log: (...args: unknown[]) => logs.push(args.map(formatResult).join(' ')),
    info: (...args: unknown[]) => logs.push(args.map(formatResult).join(' ')),
    warn: (...args: unknown[]) => logs.push(`Warning: ${args.map(formatResult).join(' ')}`),
    error: (...args: unknown[]) => logs.push(`Error: ${args.map(formatResult).join(' ')}`),
  };

  const builtIns: Record<string, unknown> = {
    Array,
    Boolean,
    Date,
    Error,
    fetch,
    isFinite,
    isNaN,
    JSON,
    Map,
    Math,
    Number,
    Object,
    parseFloat,
    parseInt,
    Promise,
    RegExp,
    Set,
    String,
    URL,
    URLSearchParams,
    console: consoleProxy,
  };

  return new Proxy(context, {
    has: () => true,
    get(target, key) {
      if (key === Symbol.unscopables) return undefined;
      if (typeof key === 'string' && key in target) return target[key];
      if (typeof key === 'string' && key in builtIns) return builtIns[key];
      return undefined;
    },
    set(target, key, value) {
      if (typeof key === 'string') target[key] = value;
      return true;
    },
  });
}

function formatJavaScript(source: string): string {
  const compacted = source
    .replace(/\s*([{}[\]();,])\s*/g, '$1')
    .replace(/;/g, ';\n')
    .replace(/{/g, '{\n')
    .replace(/}/g, '\n}\n')
    .replace(/,/g, ',\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  let depth = 0;
  return compacted
    .map((line) => {
      if (/^[}\])]/.test(line)) depth = Math.max(0, depth - 1);
      const formatted = `${'  '.repeat(depth)}${line}`;
      if (/[{[(]$/.test(line)) depth += 1;
      return formatted;
    })
    .join('\n');
}

function getCurrentToken(value: string, cursor: number): string {
  const beforeCursor = value.slice(0, cursor);
  return beforeCursor.match(/[A-Za-z_$][\w$.]*$/)?.[0] ?? '';
}

function formatResult(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'function') return value.toString();

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function makeOutput(type: OutputType, text: string): OutputLine {
  return {
    id: Date.now() + Math.random(),
    type,
    text,
  };
}
